// @ts-nocheck
import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import Ansi from "ansi-to-react";
import { useDevice, useLogStream, useLogQuery } from "../../../Api/newSockets.api";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import { savePathToLocalStorage } from "../../../utils/helpers";

const MAX_LINES = 1000;
/** Extra rows rendered above/below the viewport so fast scrolling doesn't flash blank. */
const OVERSCAN = 20;
/** Used until a real row has been rendered and measured. */
const FALLBACK_ROW_HEIGHT = 24;

/** Format a Date as a datetime-local input value ("2026-10-08T08:00"). */
const toLocalInput = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const ERROR_MESSAGES = {
  device_offline:
    "Device is offline. Historical logs are stored on the Jetson itself, so it has to be reachable.",
  log_not_found: "No log file found on the device.",
  bad_range: "Invalid range - check that the start is before the end.",
  read_failed: "The device could not read its log file.",
};

type Props = {
  match: any;
};

const Logs = (props: Props) => {
  savePathToLocalStorage();

  const deviceId = props.match.params.deviceId;
  const { getDevice } = useDevice();
  const { device } = getDevice(parseInt(deviceId));
  const { logs, isStreaming, startStream, stopStream, connected } = useLogStream(
    deviceId ? parseInt(deviceId) : null
  );
  const {
    lines: historyLines,
    loading: historyLoading,
    progress: historyProgress,
    error: historyError,
    truncated: historyTruncated,
    runQuery,
    clear: clearQuery,
  } = useLogQuery(deviceId ? parseInt(deviceId) : null);

  const [mode, setMode] = useState<"live" | "history">("live");
  const isHistory = mode === "history";

  // Default the range to the last hour, which is the most common thing to want.
  const [from, setFrom] = useState(() =>
    toLocalInput(new Date(Date.now() - 60 * 60 * 1000))
  );
  const [to, setTo] = useState(() => toLocalInput(new Date()));
  const [filter, setFilter] = useState("");

  const logsContainerRef = useRef<HTMLDivElement>(null);
  const firstRowRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const isAutoScrollingRef = useRef(false);

  /* ---------------------------------------------------------------- live mode */

  // Limit displayed logs to MAX_LINES (keep most recent)
  const displayedLogs = useMemo(() => {
    if (logs.length <= MAX_LINES) return logs;
    return logs.slice(-MAX_LINES);
  }, [logs]);

  // Calculate the starting line number offset
  const lineNumberOffset = useMemo(() => {
    return Math.max(0, logs.length - MAX_LINES);
  }, [logs.length]);

  // Auto-scroll to bottom when new logs arrive (if enabled). History mode should
  // land at the START of the queried range, so it is excluded.
  useEffect(() => {
    if (isHistory) return;
    if (autoScroll && logsContainerRef.current) {
      isAutoScrollingRef.current = true;
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
      // Reset flag after scroll completes
      setTimeout(() => {
        isAutoScrollingRef.current = false;
      }, 100);
    }
  }, [logs.length, autoScroll, isHistory]);

  // Start streaming when component mounts, stop when unmounts. Must not re-arm the
  // live tail while the user is looking at history.
  useEffect(() => {
    if (connected && deviceId && !isHistory) {
      startStream(200);
    }
    return () => {
      if (connected) {
        stopStream();
      }
    };
  }, [connected, deviceId, isHistory]);

  /* ------------------------------------------------------------- history mode */

  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [rowHeight, setRowHeight] = useState(FALLBACK_ROW_HEIGHT);

  // Measure a real row instead of hardcoding, so this survives font-size changes.
  // In history mode lines don't wrap (white-space: pre), so every row is identical.
  useLayoutEffect(() => {
    if (!isHistory) return;
    const h = firstRowRef.current?.getBoundingClientRect().height;
    if (h && Math.abs(h - rowHeight) > 0.5) setRowHeight(h);
  }, [isHistory, historyLines.length, rowHeight]);

  useLayoutEffect(() => {
    if (!isHistory) return;
    const el = logsContainerRef.current;
    if (!el) return;
    const measure = () => setViewportHeight(el.clientHeight);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isHistory]);

  const windowed = useMemo(() => {
    if (!isHistory) return null;
    const total = historyLines.length;
    const visibleCount = Math.ceil((viewportHeight || 600) / rowHeight);
    const start = Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN);
    const end = Math.min(total, start + visibleCount + OVERSCAN * 2);
    return {
      total,
      start,
      end,
      slice: historyLines.slice(start, end),
      padTop: start * rowHeight,
      padBottom: Math.max(0, (total - end) * rowHeight),
    };
  }, [isHistory, historyLines, scrollTop, viewportHeight, rowHeight]);

  const handleScroll = () => {
    if (isHistory) {
      setScrollTop(logsContainerRef.current?.scrollTop ?? 0);
      return;
    }
    // Live: if the user scrolls away from the bottom, stop following.
    if (isAutoScrollingRef.current) return;
    if (!logsContainerRef.current || !autoScroll) return;

    const container = logsContainerRef.current;
    const isAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 50;
    if (!isAtBottom) {
      setAutoScroll(false);
    }
  };

  const applyPreset = (preset: "lastHour" | "today" | "yesterday") => {
    const now = new Date();
    if (preset === "lastHour") {
      setFrom(toLocalInput(new Date(now.getTime() - 60 * 60 * 1000)));
      setTo(toLocalInput(now));
      return;
    }
    const day = new Date(now);
    if (preset === "yesterday") day.setDate(day.getDate() - 1);
    const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0);
    const end =
      preset === "yesterday"
        ? new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59)
        : now;
    setFrom(toLocalInput(start));
    setTo(toLocalInput(end));
  };

  const handleSearch = () => {
    // Release the tail -f process on the Jetson before switching away from live.
    stopStream();
    setMode("history");
    setScrollTop(0);
    if (logsContainerRef.current) logsContainerRef.current.scrollTop = 0;
    runQuery(from, to, filter.trim());
  };

  const handleBackToLive = () => {
    clearQuery();
    setMode("live");
    setAutoScroll(true);
    setScrollTop(0);
    // The mount effect re-arms the stream once isHistory flips back to false.
  };

  if (!device) {
    return <LoadingSpinner />;
  }

  const renderLine = (line: string, absoluteIndex: number, ref?: any) => (
    <div key={absoluteIndex} className="log-line" ref={ref}>
      <span className="log-line-number">{absoluteIndex + 1}</span>
      <span className="log-line-content">
        <Ansi>{line}</Ansi>
      </span>
    </div>
  );

  return (
    <div className="logs-page">
      <div className="logs-header">
        <h2>Logs - {device.name}</h2>
        <div className="logs-controls">
          {isHistory ? (
            <button className="logs-btn start" onClick={handleBackToLive}>
              Back to live
            </button>
          ) : isStreaming ? (
            <button className="logs-btn stop" onClick={stopStream}>
              Stop Streaming
            </button>
          ) : (
            <button className="logs-btn start" onClick={() => startStream(200)}>
              Start Streaming
            </button>
          )}
          {!isHistory && (
            <button
              className="logs-btn clear"
              onClick={() => {
                stopStream();
                setTimeout(() => startStream(200), 100);
              }}
            >
              Clear & Reload
            </button>
          )}
        </div>
      </div>

      <div className="logs-query">
        <div className="form-group">
          <label>From</label>
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="input-classic date-input"
          />
        </div>
        <div className="form-group">
          <label>To</label>
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="input-classic date-input"
          />
        </div>
        <div className="form-group logs-query-filter">
          <label>Filter (optional)</label>
          <input
            type="text"
            value={filter}
            placeholder="e.g. EscherUS"
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="input-classic"
          />
        </div>
        <button
          className="logs-btn search"
          onClick={handleSearch}
          disabled={historyLoading}
        >
          {historyLoading ? "Searching..." : "Search"}
        </button>
        <div className="logs-query-presets">
          <button className="logs-preset" onClick={() => applyPreset("lastHour")}>
            Last hour
          </button>
          <button className="logs-preset" onClick={() => applyPreset("today")}>
            Today
          </button>
          <button className="logs-preset" onClick={() => applyPreset("yesterday")}>
            Yesterday
          </button>
        </div>
      </div>

      <div className="logs-status">
        {isHistory ? (
          <span className="status-indicator history">History</span>
        ) : isStreaming ? (
          <span className="status-indicator streaming">Live</span>
        ) : (
          <span className="status-indicator stopped">Stopped</span>
        )}

        {isHistory ? (
          <span className="log-count">
            {historyLoading
              ? historyProgress
                ? `Loading chunk ${historyProgress.received}/${historyProgress.total}...`
                : "Querying device..."
              : `${historyLines.length} lines`}
          </span>
        ) : (
          <span className="log-count">
            {logs.length > MAX_LINES
              ? `${MAX_LINES} of ${logs.length} lines (showing latest)`
              : `${logs.length} lines`}
          </span>
        )}

        {!isHistory && (
          <label className="auto-scroll-toggle">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
            />
            Auto-scroll
          </label>
        )}

        {isHistory && historyTruncated && (
          <span className="logs-truncated">
            Result was capped - narrow the range or add a filter.
          </span>
        )}
      </div>

      {isHistory && historyError && (
        <div className="logs-error">
          {ERROR_MESSAGES[historyError] || `Query failed: ${historyError}`}
        </div>
      )}

      <div
        className={`logs-container${isHistory ? " history" : ""}`}
        ref={logsContainerRef}
        onScroll={handleScroll}
      >
        {isHistory ? (
          windowed && windowed.total > 0 ? (
            // Only the visible slice is rendered; the padding stands in for the rest
            // so the scrollbar still reflects the full range.
            <div style={{ paddingTop: windowed.padTop, paddingBottom: windowed.padBottom }}>
              {windowed.slice.map((line, i) =>
                renderLine(line, windowed.start + i, i === 0 ? firstRowRef : undefined)
              )}
            </div>
          ) : (
            <div className="logs-empty">
              {historyLoading
                ? "Querying device..."
                : historyError
                ? "No results."
                : "No log lines in that range."}
            </div>
          )
        ) : logs.length === 0 ? (
          <div className="logs-empty">
            {isStreaming ? "Waiting for logs..." : "Click 'Start Streaming' to view logs"}
          </div>
        ) : (
          displayedLogs.map((line, index) => renderLine(line, lineNumberOffset + index))
        )}
      </div>
    </div>
  );
};

export default Logs;
